import React, { useState, useEffect, useCallback } from 'react';
import { cn } from '../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Checkbox } from './ui/checkbox';
import { Separator } from './ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from './ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from './ui/tooltip';
import {
  ChevronDown,
  Upload,
  Download,
  Eye,
  Save,
  Sparkles,
  Pencil,
  Trash2,
  Plus,
  AlertTriangle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import templateService from '../services/templateService';
import quotationService from '../services/quotationService';
import excelExportService from '../services/excelExportService';

// Display helpers: show actual DB property names for common fields
const DB_HEADER_FIELD_PATHS = {
  quotation_number: 'quotation_number',
  client_name: 'client_name',
  quotation_date: 'quotation_date',
  status: 'status',
  total_amount: 'total_amount',
  valid_until: 'valid_until',
  contact_person: 'contact_person',
  phone: 'phone',
  address: 'address',
  remarks: 'remarks'
};

const DB_COLUMN_FIELD_PATHS = {
  sequence: 'auto.sequence',
  name: 'products[].name',
  specification: 'products[].specification',
  specifications: 'products[].specification',
  unit: 'products[].unit',
  quantity: 'products[].quantity',
  unit_price: 'products[].unit_price',
  total_price: 'products[].total_price',
  remarks: 'products[].remarks',
  product_id: 'products[].product_id',
  __computed_unit_price: '__computed.unit_price',
  __computed_total_price: '__computed.total_price'
};

// Fallback formatter options for custom DB fields
const GENERIC_FORMATTERS = ['text', 'number', 'currency', 'date_chinese', 'date_iso', 'date_us'];

const FIELD_OPTIONS = {
  headers: [
    { value: 'quotation_number', label: '报价单号', formatters: ['text'] },
    { value: 'client_name', label: '客户名称', formatters: ['text'] },
    { value: 'quotation_date', label: '报价日期', formatters: ['date_chinese', 'date_iso', 'date_us'] },
    { value: 'status', label: '状态', formatters: ['text'] },
    { value: 'total_amount', label: '总金额', formatters: ['currency', 'number'] },
    { value: 'valid_until', label: '有效期至', formatters: ['date_chinese', 'date_iso', 'date_us'] },
    { value: 'contact_person', label: '联系人', formatters: ['text'] },
    { value: 'phone', label: '电话', formatters: ['text'] },
    { value: 'address', label: '地址', formatters: ['text'] },
    { value: 'remarks', label: '备注', formatters: ['text'] }
  ],
  columns: [
    { value: 'sequence', label: '序号', formatters: ['number'] },
    { value: 'name', label: '名称', formatters: ['text'] },
    { value: 'specification', label: '规格', formatters: ['text'] },
    { value: 'unit', label: '单位', formatters: ['text'] },
    { value: 'quantity', label: '数量', formatters: ['number'] },
    { value: 'unit_price', label: '单价', formatters: ['currency', 'number'] },
    { value: 'total_price', label: '金额', formatters: ['currency', 'number'] },
    { value: 'remarks', label: '备注', formatters: ['text'] },
    { value: 'product_id', label: '货号', formatters: ['text'] },
    { value: '__computed_unit_price', label: '计算单价', formatters: ['computed'] },
    { value: '__computed_total_price', label: '计算金额', formatters: ['computed'] }
  ]
};

const HEADER_PATTERNS = {
  '报价单号': ['quotation_number', '报价单号', '报价编号', 'Quotation No'],
  '客户名称': ['client_name', '客户', '客户名称', 'Client', 'Customer'],
  '报价日期': ['quotation_date', '日期', '报价日期', 'Date', 'Quotation Date'],
  '状态': ['status', '状态', 'Status'],
  '总金额': ['total_amount', '总金额', '合计', '总计', 'Total', 'Amount'],
  '有效期至': ['valid_until', '有效期', '有效期至', 'Valid Until'],
  '联系人': ['contact_person', '联系人', 'Contact'],
  '电话': ['phone', '电话', '手机', 'Phone', 'Tel'],
  '地址': ['address', '地址', '地点', 'Address'],
  '备注': ['remarks', '备注', '说明', 'Remarks', 'Note']
};

const COLUMN_PATTERNS = {
  '序号': ['sequence', '序号', '编号', 'No.', '#'],
  '名称': ['name', '名称', '产品名称', '产品', 'Product', 'Name'],
  '规格': ['specification', '规格', '型号', 'Specification', 'Model'],
  '货号': ['product_id', '货号', '产品编号', '产品货号', '料号', '物料号', 'Product ID', 'SKU'],
  '单位': ['unit', '单位', '计量单位', 'Unit', 'UOM'],
  '数量': ['quantity', '数量', '数', 'Quantity', 'Qty'],
  '单价': ['unit_price', '单价', '价格', 'Unit Price', 'Price'],
  '金额': ['total_price', '金额', '小计', '总价', 'Amount', 'Total'],
  '备注': ['remarks', '备注', '说明', 'Remarks', 'Note']
};

const TemplateAnnotator = ({ open, onClose, template, onSave }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [warnings, setWarnings] = useState([]);

  // Template data
  const [templateData, setTemplateData] = useState(null);

  // Metadata
  const [metadata, setMetadata] = useState({
    header_mappings: [],
    table_config: {
      start_row: 8,
      prototype_row: 8,
      columns: []
    },
    detected_headers: {},
    detected_columns: {},
    embed_metadata: false
  });

  // Preview data
  const [previewData, setPreviewData] = useState(null);
  const [selectedQuotation, setSelectedQuotation] = useState('');
  const [quotations, setQuotations] = useState([]);

  // DB path edit dialog state
  const [dbEditOpen, setDbEditOpen] = useState(false);
  const [dbEditMode, setDbEditMode] = useState('header'); // 'header' | 'column'
  const [dbEditTarget, setDbEditTarget] = useState(null); // cell ref or column letter
  const [dbEditValue, setDbEditValue] = useState('');
  const [dbEditFormatter, setDbEditFormatter] = useState('text');
  const [dbEditSuggestions, setDbEditSuggestions] = useState([]);
  const [dbEditLoading, setDbEditLoading] = useState(false);

  // Collapsible states
  const [headersExpanded, setHeadersExpanded] = useState(true);
  const [columnsExpanded, setColumnsExpanded] = useState(false);
  const [headerMappingsExpanded, setHeaderMappingsExpanded] = useState(true);
  const [tableConfigExpanded, setTableConfigExpanded] = useState(false);

  // Auto-suggest default total price formula when quantity and unit price are mapped
  useEffect(() => {
    const cols = metadata?.table_config?.columns || [];
    if (!cols.length) return;
    const qty = cols.find(c => c.field === 'quantity');
    const price = cols.find(c => c.field === 'unit_price' || c.field === 'price');
    // Prefer computed total field if present; otherwise allow total_price
    const total = cols.find(c => c.field === '__computed_total_price') || cols.find(c => c.field === 'total_price');
    if (qty && price && total && !total.formula) {
      const formula = `=${qty.column}{row}*${price.column}{row}`;
      setMetadata(prev => {
        const list = [...prev.table_config.columns];
        const idx = list.findIndex(c => c.column === total.column);
        if (idx >= 0 && !list[idx].formula) {
          list[idx] = { ...list[idx], formula };
          return { ...prev, table_config: { ...prev.table_config, columns: list } };
        }
        return prev;
      });
    }
  }, [metadata.table_config.columns]);

  const steps = [
    'Upload/Select Template',
    'Auto-Detect Structure',
    'Manual Adjustment',
    'Preview & Validate',
    'Save & Version'
  ];

  const parseTemplateStructure = useCallback(async (templateInfo, options = {}) => {
    try {
      setLoading(true);
      setError('');

      // Load template data
      setTemplateData(template);

      // Load existing metadata if available
      let hasSavedHeaders = false;
      let hasSavedColumns = false;
      try {
        const metadataResponse = await templateService.getTemplateMetadata(template._id);
        if (metadataResponse) {
          const metaRaw = metadataResponse.data || metadataResponse;
          const meta = normalizeMetadata(metaRaw);
          if (meta && Object.keys(meta).length > 0) {
            hasSavedHeaders = Array.isArray(meta.header_mappings) && meta.header_mappings.length > 0;
            hasSavedColumns = Array.isArray(meta.table_config?.columns) && meta.table_config.columns.length > 0;
            setMetadata(prev => ({
              ...prev,
              ...meta
            }));
          }
        }
      } catch (e) {
        console.warn('Failed to fetch existing metadata, proceeding with detection:', e);
      }

      // Parse template structure, preserving saved mappings if present
      await parseTemplateStructure(template, { preserveMappings: hasSavedHeaders || hasSavedColumns });

      setLoading(false);
    } catch (err) {
      setError(`Failed to load template: ${err.message}`);
      setLoading(false);
    }
  }, [template]);

  const loadTemplate = useCallback(async () => {
    try {
      setLoading(true);

      // This would normally parse the Excel file to extract structure
      // For now, we'll simulate with detected structure
      const detectedData = await detectTemplateStructure(template);

      // Store sheet data for potential future use
      // setSheetData(detectedData.sheetData);
      setMetadata(prev => {
        const updated = { ...prev };
        // Always keep detected info for reference
        updated.detected_headers = detectedData.headers;
        updated.detected_columns = detectedData.columns;
        // Only set start/prototype rows if not already defined (preserve saved values)
        updated.table_config = {
          ...prev.table_config,
          start_row: prev.table_config?.start_row || detectedData.tableStartRow || 8,
          prototype_row: prev.table_config?.prototype_row || detectedData.prototypeRow || (prev.table_config?.start_row || 8)
        };
        return updated;
      });

      // Only auto-map when there are no saved mappings or when preserveMappings is false
      const preserve = false; // Default behavior for loadTemplate
      if (!preserve) {
        autoMapFields(detectedData);
      }

      setLoading(false);
    } catch (err) {
      setError(`Failed to parse template: ${err.message}`);
      setLoading(false);
    }
  }, [template]);

  useEffect(() => {
    if (open && template) {
      loadTemplate();
    }
  }, [open, template, loadTemplate]);

  const detectTemplateStructure = async (templateInfo) => {
    // Simulate Excel structure detection
    // In production, this would call a backend API that uses openpyxl to read the template

    const mockSheetData = {
      rows: [
        { row: 1, cells: { A: '', B: '报价单', C: '', D: '', E: '', F: '', G: '' } },
        { row: 2, cells: { A: '报价单号:', B: '', C: '客户名称:', D: '', E: '报价日期:', F: '', G: '' } },
        { row: 3, cells: { A: '联系人:', B: '', C: '电话:', D: '', E: '地址:', F: '', G: '' } },
        { row: 4, cells: { A: '有效期至:', B: '', C: '状态:', D: '', E: '总金额:', F: '', G: '' } },
        { row: 5, cells: { A: '', B: '', C: '', D: '', E: '', F: '', G: '' } },
        { row: 6, cells: { A: '产品明细', B: '', C: '', D: '', E: '', F: '', G: '' } },
        { row: 7, cells: { A: '序号', B: '名称', C: '规格', D: '单位', E: '数量', F: '单价', G: '金额' } },
        { row: 8, cells: { A: '1', B: '', C: '', D: '', E: '', F: '', G: '' } }
      ]
    };

    // Detect headers
    const detectedHeaders = {};
    mockSheetData.rows.forEach(row => {
      Object.entries(row.cells).forEach(([col, value]) => {
        if (value && typeof value === 'string') {
          const cleanValue = value.replace(/[:：]/g, '').trim();
          Object.entries(HEADER_PATTERNS).forEach(([field, patterns]) => {
            patterns.forEach(pattern => {
              if (cleanValue.toLowerCase().includes(pattern.toLowerCase())) {
                detectedHeaders[`${col}${row.row}`] = {
                  cell: `${col}${row.row}`,
                  field: patterns[0],
                  label: cleanValue,
                  confidence: 0.9
                };
              }
            });
          });
        }
      });
    });

    // Detect table columns
    const tableHeaderRow = 7;
    const detectedColumns = {};
    const headerRow = mockSheetData.rows.find(r => r.row === tableHeaderRow);
    if (headerRow) {
      Object.entries(headerRow.cells).forEach(([col, value]) => {
        if (value && typeof value === 'string') {
          Object.entries(COLUMN_PATTERNS).forEach(([field, patterns]) => {
            patterns.forEach(pattern => {
              if (value.toLowerCase().includes(pattern.toLowerCase())) {
                detectedColumns[col] = {
                  column: col,
                  field: patterns[0],
                  label: value,
                  confidence: 0.9
                };
              }
            });
          });
        }
      });
    }

    return {
      sheetData: mockSheetData,
      headers: detectedHeaders,
      columns: detectedColumns,
      tableStartRow: 8,
      prototypeRow: 8
    };
  };

  const autoMapFields = (detectedData) => {
    // Auto-map header fields
    const headerMappings = [];
    Object.entries(detectedData.headers).forEach(([cell, info]) => {
      if (info.confidence > 0.7) {
        const fieldOption = FIELD_OPTIONS.headers.find(f => f.value === info.field);
        headerMappings.push({
          cell: info.cell,
          field: info.field,
          formatter: fieldOption?.formatters[0] || 'text',
          confidence: info.confidence
        });
      }
    });

    // Auto-map column fields
    const columnMappings = [];
    Object.entries(detectedData.columns).forEach(([col, info]) => {
      if (info.confidence > 0.7) {
        const fieldOption = FIELD_OPTIONS.columns.find(f => f.value === info.field);
        columnMappings.push({
          column: info.column,
          field: info.field,
          formatter: fieldOption?.formatters[0] || 'text',
          confidence: info.confidence
        });
      }
    });

    setMetadata(prev => ({
      ...prev,
      header_mappings: headerMappings,
      table_config: {
        ...prev.table_config,
        columns: columnMappings
      }
    }));
  };

  const handleHeaderMapping = (cell, field, formatter) => {
    setMetadata(prev => {
      const mappings = [...prev.header_mappings];
      const existingIndex = mappings.findIndex(m => m.cell === cell);

      if (field === '' || field === 'none') {
        // Remove mapping
        if (existingIndex >= 0) {
          mappings.splice(existingIndex, 1);
        }
      } else {
        // Add or update mapping
        const mapping = { cell, field, formatter: formatter || 'text' };
        if (existingIndex >= 0) {
          mappings[existingIndex] = mapping;
        } else {
          mappings.push(mapping);
        }
      }

      return { ...prev, header_mappings: mappings };
    });
  };

  const handleColumnMapping = (column, field, formatter, dbPath = undefined) => {
    setMetadata(prev => {
      const columns = [...prev.table_config.columns];
      const existingIndex = columns.findIndex(c => c.column === column);

      if (field === '' || field === 'none') {
        // Remove mapping
        if (existingIndex >= 0) {
          columns.splice(existingIndex, 1);
        }
      } else {
        // Add or update mapping
        const mapping = { column, field, formatter: formatter || 'text' };
        if (dbPath) {
          mapping.db_path = dbPath;
        } else if (existingIndex >= 0 && columns[existingIndex].db_path) {
          // Preserve existing custom db_path if present
          mapping.db_path = columns[existingIndex].db_path;
        }
        if (existingIndex >= 0) {
          columns[existingIndex] = mapping;
        } else {
          columns.push(mapping);
        }
      }

      return {
        ...prev,
        table_config: {
          ...prev.table_config,
          columns
        }
      };
    });
  };

  const loadSampleQuotations = async () => {
    try {
      const response = await quotationService.getQuotations({ page: 1, page_size: 10 });
      setQuotations(response.quotations || []);
    } catch (err) {
      console.error('Failed to load quotations:', err);
    }
  };

  const generatePreview = async () => {
    if (!selectedQuotation) {
      setWarnings(['Please select a quotation for preview']);
      return;
    }

    try {
      setLoading(true);

      // Get quotation details
      const quotation = await quotationService.getQuotation(selectedQuotation);

      // Generate preview data based on metadata
      const preview = {
        headers: {},
        rows: []
      };

      // Map header fields
      metadata.header_mappings.forEach(mapping => {
        const value = quotation[mapping.field];
        preview.headers[mapping.cell] = formatValue(value, mapping.formatter);
      });

      // Map table rows
      if (quotation.products && quotation.products.length > 0) {
        quotation.products.forEach((product, index) => {
          const row = {};
          metadata.table_config.columns.forEach(col => {
            let value = product[col.field];

            // Handle computed fields
            if (col.field === '__computed_unit_price') {
              value = product.unit_price || product.price || 0;
            } else if (col.field === '__computed_total_price') {
              value = product.total_price || (product.quantity * (product.unit_price || product.price || 0));
            } else if (col.field === 'sequence') {
              value = index + 1;
            }

            row[col.column] = formatValue(value, col.formatter);
          });
          preview.rows.push(row);
        });
      }

      setPreviewData(preview);
      setLoading(false);

      // Validate preview
      validatePreview(preview);
    } catch (err) {
      setError(`Failed to generate preview: ${err.message}`);
      setLoading(false);
    }
  };

  const formatValue = (value, formatter) => {
    if (value === null || value === undefined) return '';

    switch (formatter) {
      case 'currency':
        return typeof value === 'number' ? `¥${value.toFixed(2)}` : value;
      case 'number':
        return typeof value === 'number' ? value.toString() : value;
      case 'date_chinese':
        if (value instanceof Date || typeof value === 'string') {
          const date = new Date(value);
          return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
        }
        return value;
      case 'date_iso':
        if (value instanceof Date || typeof value === 'string') {
          return new Date(value).toISOString().split('T')[0];
        }
        return value;
      case 'computed':
        return '[Computed]';
      default:
        return value?.toString() || '';
    }
  };

  const validatePreview = (preview) => {
    const validationWarnings = [];

    // Check for empty required fields
    const requiredHeaders = ['quotation_number', 'client_name', 'quotation_date'];
    requiredHeaders.forEach(field => {
      const mapping = metadata.header_mappings.find(m => m.field === field);
      if (!mapping) {
        validationWarnings.push(`Required field "${field}" is not mapped`);
      } else if (!preview.headers[mapping.cell]) {
        validationWarnings.push(`Field "${field}" is empty in preview`);
      }
    });

    // Check table columns
    const requiredColumns = ['name', 'quantity', 'unit_price'];
    requiredColumns.forEach(field => {
      const mapping = metadata.table_config.columns.find(c => c.field === field);
      if (!mapping && !metadata.table_config.columns.find(c => c.field === `__computed_${field}`)) {
        validationWarnings.push(`Required column "${field}" is not mapped`);
      }
    });

    // Check numeric fields
    metadata.table_config.columns.forEach(col => {
      if (['unit_price', 'total_price', 'quantity'].includes(col.field)) {
        preview.rows.forEach((row, index) => {
          const value = row[col.column];
          if (value && isNaN(parseFloat(value.replace(/[¥,]/g, '')))) {
            validationWarnings.push(`Row ${index + 1}: "${col.field}" is not numeric`);
          }
        });
      }
    });

    setWarnings(validationWarnings);
  };

  const handleExportJSON = () => {
    templateService.exportMetadataAsJSON(metadata, template?.name);
  };

  const handleImportJSON = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const importedMetadata = await templateService.importMetadataFromJSON(file);
      setMetadata(prev => ({
        ...prev,
        ...importedMetadata
      }));
      setError('');
    } catch (err) {
      setError('Invalid JSON file');
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // Save metadata to template
      const normalized = normalizeMetadata(metadata);
      await templateService.updateTemplateMetadata(template._id, normalized);

      // If embed_metadata is checked, update the template file
      if (normalized.embed_metadata) {
        await templateService.embedMetadataInTemplate(template._id, normalized);
      }

      setLoading(false);
      onSave(normalized);
      onClose();
    } catch (err) {
      setError(`Failed to save metadata: ${err.message}`);
      setLoading(false);
    }
  };

  // Normalize metadata fields for consistency
  const normalizeMetadata = (meta) => {
    if (!meta || typeof meta !== 'object') return meta;
    const cloned = JSON.parse(JSON.stringify(meta));
    // Normalize columns array
    if (cloned.table_config && Array.isArray(cloned.table_config.columns)) {
      cloned.table_config.columns = cloned.table_config.columns.map(col => {
        const c = { ...col };
        if (c.field === 'specifications') c.field = 'specification';
        if (typeof c.db_path === 'string' && c.db_path.includes('products[].specifications')) {
          c.db_path = c.db_path.replace('products[].specifications', 'products[].specification');
        }
        return c;
      });
    }
    return cloned;
  };

  const handleNext = () => {
    if (activeStep === 3) {
      // Generate preview before moving to save step
      generatePreview();
    }
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <div>
            <h3 className="text-lg font-semibold mb-4">Template Information</h3>
            {templateData && (
              <div className="space-y-2">
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium">Template Name</span>
                  <span className="text-muted-foreground">{templateData.name}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium">Entity Type</span>
                  <span className="text-muted-foreground">{templateData.entity_type}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium">Language</span>
                  <span className="text-muted-foreground">{templateData.language || 'Chinese'}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium">Description</span>
                  <span className="text-muted-foreground">{templateData.description}</span>
                </div>
              </div>
            )}
          </div>
        );

      case 1:
        return (
          <div>
            <h3 className="text-lg font-semibold mb-4">Auto-Detected Structure</h3>

            <Alert className="mb-4">
              <AlertTitle>Auto-Detection Results</AlertTitle>
              <AlertDescription>
                The system has automatically detected the following structure. You can adjust these in the next step.
              </AlertDescription>
            </Alert>

            <Collapsible open={headersExpanded} onOpenChange={setHeadersExpanded} className="border rounded-lg mb-2">
              <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50">
                <span className="font-medium">Detected Headers ({Object.keys(metadata.detected_headers).length})</span>
                <ChevronDown className={cn("h-4 w-4 transition-transform", headersExpanded && "rotate-180")} />
              </CollapsibleTrigger>
              <CollapsibleContent className="px-4 pb-4">
                <div className="space-y-2">
                  {Object.entries(metadata.detected_headers).map(([cell, info]) => (
                    <div key={cell} className="flex items-center justify-between py-2 border-b">
                      <div>
                        <p className="font-medium">Cell {info.cell}: {info.label}</p>
                        <p className="text-sm text-muted-foreground">
                          Mapped to: {info.field} (Confidence: {(info.confidence * 100).toFixed(0)}%)
                        </p>
                      </div>
                      {info.confidence > 0.8 && <CheckCircle className="h-5 w-5 text-success" />}
                      {info.confidence <= 0.8 && info.confidence > 0.5 && <AlertTriangle className="h-5 w-5 text-warning" />}
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>

            <Collapsible open={columnsExpanded} onOpenChange={setColumnsExpanded} className="border rounded-lg">
              <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50">
                <span className="font-medium">Detected Table Columns ({Object.keys(metadata.detected_columns).length})</span>
                <ChevronDown className={cn("h-4 w-4 transition-transform", columnsExpanded && "rotate-180")} />
              </CollapsibleTrigger>
              <CollapsibleContent className="px-4 pb-4">
                <div className="space-y-2">
                  {Object.entries(metadata.detected_columns).map(([col, info]) => (
                    <div key={col} className="flex items-center justify-between py-2 border-b">
                      <div>
                        <p className="font-medium">Column {info.column}: {info.label}</p>
                        <p className="text-sm text-muted-foreground">
                          Mapped to: {info.field} (Confidence: {(info.confidence * 100).toFixed(0)}%)
                        </p>
                      </div>
                      {info.confidence > 0.8 && <CheckCircle className="h-5 w-5 text-success" />}
                      {info.confidence <= 0.8 && info.confidence > 0.5 && <AlertTriangle className="h-5 w-5 text-warning" />}
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>

            <div className="mt-4 space-y-1">
              <p className="text-sm text-muted-foreground">
                Table Start Row: {metadata.table_config.start_row}
              </p>
              <p className="text-sm text-muted-foreground">
                Prototype Row: {metadata.table_config.prototype_row}
              </p>
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Manual Adjustment</h3>
            <p className="text-sm text-muted-foreground mb-4">
              You can map to actual DB property paths. Use the edit icon to enter a custom path (e.g., "client_name" or "products[].name").
            </p>

            <Collapsible open={headerMappingsExpanded} onOpenChange={setHeaderMappingsExpanded} className="border rounded-lg mb-2">
              <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50">
                <span className="font-medium">Header Mappings</span>
                <ChevronDown className={cn("h-4 w-4 transition-transform", headerMappingsExpanded && "rotate-180")} />
              </CollapsibleTrigger>
              <CollapsibleContent className="px-4 pb-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-2 font-medium">Cell</th>
                        <th className="text-left py-2 px-2 font-medium">Field</th>
                        <th className="text-left py-2 px-2 font-medium">DB Property</th>
                        <th className="text-left py-2 px-2 font-medium">Formatter</th>
                        <th className="text-left py-2 px-2 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metadata.header_mappings.map((mapping, index) => (
                        <tr key={index} className="border-b">
                          <td className="py-2 px-2">{mapping.cell}</td>
                          <td className="py-2 px-2">
                            <Select
                              value={mapping.field}
                              onValueChange={(value) => handleHeaderMapping(mapping.cell, value, mapping.formatter)}
                            >
                              <SelectTrigger className="w-full h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                {FIELD_OPTIONS.headers.map(option => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex items-center gap-1">
                              <Badge variant="outline" className="text-xs">
                                {DB_HEADER_FIELD_PATHS[mapping.field] || mapping.field || '-'}
                              </Badge>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6"
                                      onClick={() => {
                                        const current = DB_HEADER_FIELD_PATHS[mapping.field] || mapping.field || '';
                                        setDbEditMode('header');
                                        setDbEditTarget(mapping.cell);
                                        setDbEditFormatter((FIELD_OPTIONS.headers.find(f => f.value === mapping.field)?.formatters?.[0]) || mapping.formatter || 'text');
                                        setDbEditValue(current);
                                        setDbEditOpen(true);
                                      }}
                                    >
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Set custom DB property path</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            <Select
                              value={mapping.formatter}
                              onValueChange={(value) => handleHeaderMapping(mapping.cell, mapping.field, value)}
                            >
                              <SelectTrigger className="w-full h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {(FIELD_OPTIONS.headers.find(f => f.value === mapping.field)?.formatters || GENERIC_FORMATTERS)
                                  .map(fmt => (
                                    <SelectItem key={fmt} value={fmt}>{fmt}</SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-2 px-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleHeaderMapping(mapping.cell, '', '')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newCell = prompt('Enter cell reference (e.g., B2):');
                    if (newCell) {
                      handleHeaderMapping(newCell, 'quotation_number', 'text');
                    }
                  }}
                  className="mt-2"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Header Mapping
                </Button>
              </CollapsibleContent>
            </Collapsible>

            <Collapsible open={tableConfigExpanded} onOpenChange={setTableConfigExpanded} className="border rounded-lg">
              <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50">
                <span className="font-medium">Table Configuration</span>
                <ChevronDown className={cn("h-4 w-4 transition-transform", tableConfigExpanded && "rotate-180")} />
              </CollapsibleTrigger>
              <CollapsibleContent className="px-4 pb-4">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="space-y-2">
                    <Label>Table Start Row</Label>
                    <Input
                      type="number"
                      value={metadata.table_config.start_row}
                      onChange={(e) => setMetadata(prev => ({
                        ...prev,
                        table_config: {
                          ...prev.table_config,
                          start_row: parseInt(e.target.value)
                        }
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Prototype Row</Label>
                    <Input
                      type="number"
                      value={metadata.table_config.prototype_row}
                      onChange={(e) => setMetadata(prev => ({
                        ...prev,
                        table_config: {
                          ...prev.table_config,
                          prototype_row: parseInt(e.target.value)
                        }
                      }))}
                    />
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-2 font-medium">Column</th>
                        <th className="text-left py-2 px-2 font-medium">Field</th>
                        <th className="text-left py-2 px-2 font-medium">DB Property</th>
                        <th className="text-left py-2 px-2 font-medium">Formatter</th>
                        <th className="text-left py-2 px-2 font-medium">Formula</th>
                        <th className="text-left py-2 px-2 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metadata.table_config.columns.map((column, index) => (
                        <tr key={index} className="border-b">
                          <td className="py-2 px-2">{column.column}</td>
                          <td className="py-2 px-2">
                            <Select
                              value={column.field}
                              onValueChange={(value) => handleColumnMapping(column.column, value, column.formatter)}
                            >
                              <SelectTrigger className="w-full h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                {FIELD_OPTIONS.columns.map(option => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex items-center gap-1">
                              <Badge variant="outline" className="text-xs">
                                {column.db_path || DB_COLUMN_FIELD_PATHS[column.field] || column.field || '-'}
                              </Badge>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6"
                                      onClick={() => {
                                        const current = column.db_path || DB_COLUMN_FIELD_PATHS[column.field] || column.field || '';
                                        setDbEditMode('column');
                                        setDbEditTarget(column.column);
                                        setDbEditFormatter((FIELD_OPTIONS.columns.find(f => f.value === column.field)?.formatters?.[0]) || column.formatter || 'text');
                                        setDbEditValue(current);
                                        setDbEditOpen(true);
                                      }}
                                    >
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Set custom DB property path for this column</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            <Select
                              value={column.formatter}
                              onValueChange={(value) => handleColumnMapping(column.column, column.field, value)}
                            >
                              <SelectTrigger className="w-full h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {(FIELD_OPTIONS.columns.find(f => f.value === column.field)?.formatters || GENERIC_FORMATTERS)
                                  .map(fmt => (
                                    <SelectItem key={fmt} value={fmt}>{fmt}</SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-2 px-2">
                            <Input
                              value={column.formula || ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                setMetadata(prev => {
                                  const cols = [...prev.table_config.columns];
                                  const idx = cols.findIndex(c => c.column === column.column);
                                  if (idx >= 0) {
                                    cols[idx] = { ...cols[idx], formula: val };
                                  }
                                  return {
                                    ...prev,
                                    table_config: { ...prev.table_config, columns: cols }
                                  };
                                });
                              }}
                              placeholder="=E{row}*F{row}"
                              className="h-8"
                            />
                          </td>
                          <td className="py-2 px-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleColumnMapping(column.column, '', '')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newColumn = prompt('Enter column letter (e.g., A):');
                    if (newColumn) {
                      handleColumnMapping(newColumn, 'name', 'text');
                    }
                  }}
                  className="mt-2"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Column Mapping
                </Button>
              </CollapsibleContent>
            </Collapsible>
          </div>
        );

      case 3:
        return (
          <div>
            <h3 className="text-lg font-semibold mb-4">Preview & Validate</h3>

            {warnings.length > 0 && (
              <Alert variant="warning" className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Validation Warnings</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc pl-4 mt-2">
                    {warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="mb-4 space-y-2">
              <Label>Select Quotation for Preview</Label>
              <Select value={selectedQuotation} onValueChange={setSelectedQuotation}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a quotation" />
                </SelectTrigger>
                <SelectContent>
                  {quotations.map(q => (
                    <SelectItem key={q._id} value={q._id}>
                      {q.quotation_number} - {q.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                onClick={generatePreview}
                disabled={!selectedQuotation}
                className="mt-2"
              >
                <Eye className="h-4 w-4 mr-2" />
                Generate Preview
              </Button>
            </div>

            {previewData && (
              <div>
                <h4 className="font-medium mb-2">Header Fields Preview</h4>
                <div className="border rounded-lg overflow-hidden mb-4">
                  <table className="w-full text-sm">
                    <tbody>
                      {Object.entries(previewData.headers).map(([cell, value]) => (
                        <tr key={cell} className="border-b last:border-b-0">
                          <td className="py-2 px-4 bg-muted/50 font-medium">{cell}</td>
                          <td className="py-2 px-4">{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h4 className="font-medium mb-2">Table Rows Preview</h4>
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        {metadata.table_config.columns.map(col => (
                          <th key={col.column} className="py-2 px-4 text-left font-medium">
                            {col.column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.rows.slice(0, 5).map((row, index) => (
                        <tr key={index} className="border-b last:border-b-0">
                          {metadata.table_config.columns.map(col => (
                            <td key={col.column} className="py-2 px-4">
                              {row[col.column]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div>
            <h3 className="text-lg font-semibold mb-4">Save & Version</h3>

            <Alert className="mb-4 border-success/20 bg-success/10 text-success">
              <CheckCircle className="h-4 w-4" />
              <AlertTitle>Ready to Save</AlertTitle>
              <AlertDescription>
                Your template metadata is ready to be saved. You can optionally embed the metadata in the Excel file for portability.
              </AlertDescription>
            </Alert>

            <div className="mb-4 flex items-center space-x-2">
              <Checkbox
                id="embed-metadata"
                checked={metadata.embed_metadata}
                onCheckedChange={(checked) => setMetadata(prev => ({
                  ...prev,
                  embed_metadata: checked
                }))}
              />
              <Label htmlFor="embed-metadata">
                Embed metadata in Excel file (recommended for portability)
              </Label>
            </div>

            <Separator className="my-4" />

            <h4 className="font-medium mb-2">Import/Export Options</h4>

            <div className="flex gap-2">
              <Button variant="outline" onClick={handleExportJSON}>
                <Download className="h-4 w-4 mr-2" />
                Export Metadata JSON
              </Button>

              <Button variant="outline" asChild>
                <label className="cursor-pointer">
                  <Upload className="h-4 w-4 mr-2" />
                  Import Metadata JSON
                  <input
                    type="file"
                    accept=".json"
                    className="hidden"
                    onChange={handleImportJSON}
                  />
                </label>
              </Button>
            </div>

            <Separator className="my-4" />

            <p className="text-sm text-muted-foreground mb-2">Summary:</p>
            <div className="space-y-2">
              <div className="flex justify-between py-2 border-b">
                <span className="font-medium">Header Mappings</span>
                <span className="text-muted-foreground">{metadata.header_mappings.length} fields mapped</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="font-medium">Table Columns</span>
                <span className="text-muted-foreground">{metadata.table_config.columns.length} columns configured</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="font-medium">Table Configuration</span>
                <span className="text-muted-foreground">
                  Start Row: {metadata.table_config.start_row}, Prototype Row: {metadata.table_config.prototype_row}
                </span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    if (activeStep === 3 && quotations.length === 0) {
      loadSampleQuotations();
    }
  }, [activeStep, quotations.length]);

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Template Annotator - {template?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-1">
            {/* Custom Stepper */}
            <div className="flex items-center justify-between mb-6">
              {steps.map((label, index) => (
                <div key={label} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div className={cn(
                      "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium",
                      index <= activeStep
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                    )}>
                      {index + 1}
                    </div>
                    <span className={cn(
                      "mt-1 text-xs max-w-[80px] text-center",
                      index <= activeStep ? "text-primary" : "text-muted-foreground"
                    )}>
                      {label}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={cn(
                      "w-8 h-0.5 mx-1",
                      index < activeStep ? "bg-primary" : "bg-muted"
                    )} />
                  )}
                </div>
              ))}
            </div>

            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : (
              renderStepContent()
            )}
          </div>

          <DialogFooter className="flex justify-between">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={activeStep === 0}
                onClick={handleBack}
              >
                Back
              </Button>
              {activeStep === steps.length - 1 ? (
                <Button onClick={handleSave} disabled={loading}>
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <Save className="h-4 w-4 mr-2" />
                  Save Metadata
                </Button>
              ) : (
                <Button onClick={handleNext}>
                  Next
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DB Path Edit Dialog */}
      <Dialog open={dbEditOpen} onOpenChange={setDbEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set DB Property Path</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {dbEditMode === 'header' ? 'Header field path (e.g., client_name or client.name)' : 'Row field path (e.g., products[].name)'}
            </p>
            <Input
              value={dbEditValue}
              onChange={async (e) => {
                const val = e.target.value;
                setDbEditValue(val);
                // Fetch suggestions based on last segment typed
                const termMatch = val.match(/([^.[\]]+)$/);
                const term = termMatch ? termMatch[1] : val;
                setDbEditLoading(true);
                const suggestions = await excelExportService.getFieldSuggestions(term);
                setDbEditSuggestions(suggestions);
                setDbEditLoading(false);
              }}
              placeholder={dbEditMode === 'header' ? 'client_name' : 'products[].name'}
            />
            <div>
              <p className="text-xs text-muted-foreground mb-1">Suggestions</p>
              <div className="border rounded-lg max-h-48 overflow-auto">
                {dbEditLoading && (
                  <div className="p-2 text-sm text-muted-foreground">Loading...</div>
                )}
                {!dbEditLoading && dbEditSuggestions.length === 0 && (
                  <div className="p-2 text-sm text-muted-foreground">No suggestions</div>
                )}
                {!dbEditLoading && dbEditSuggestions.map((sug, idx) => (
                  <div
                    key={idx}
                    className="p-2 hover:bg-muted cursor-pointer border-b last:border-b-0"
                    onClick={() => {
                      if (dbEditMode === 'header') {
                        setDbEditValue(sug.field_name);
                      } else {
                        setDbEditValue(`products[].${sug.field_name}`);
                      }
                    }}
                  >
                    <p className="font-medium text-sm">{sug.field_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {sug.excel_header ? `Header: ${sug.excel_header}${sug.category ? ` · ${sug.category}` : ''}` : sug.category}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDbEditOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                const raw = (dbEditValue || '').trim();
                if (!raw) { setDbEditOpen(false); return; }
                if (dbEditMode === 'header') {
                  handleHeaderMapping(dbEditTarget, raw, dbEditFormatter);
                } else {
                  const lastSegmentMatch = raw.match(/([^.[\]]+)$/);
                  const itemField = lastSegmentMatch ? lastSegmentMatch[1] : raw;
                  handleColumnMapping(dbEditTarget, itemField, dbEditFormatter, raw);
                }
                setDbEditOpen(false);
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TemplateAnnotator;
